import type { TaskOperatorReadModel } from "@/hooks/use-tasks";
import { cn } from "@/lib/utils";

type CrmTaskContextCompleteness = TaskOperatorReadModel["contextCompleteness"];

export type CrmTaskContextCompletenessStatus = "ready" | "partial" | "missing";

export interface CrmTaskAssigneeOption {
  id: string;
  label: string;
}

interface CrmTaskContextQualityCardProps {
  contextCompleteness: CrmTaskContextCompleteness | null | undefined;
}

interface CrmTaskAssigneeRoutingCardProps {
  assigneeLabel: string | null;
  assigneeDraftId: string;
  assigneeOptions: CrmTaskAssigneeOption[];
  isAssigneeDraftKnown: boolean;
  isClosed: boolean;
  isUpdatingAssignee: boolean;
  hasAssigneeDraftChanged: boolean;
  feedback: string | null;
  onAssigneeDraftChange: (assigneeId: string) => void;
  onSaveAssignee: () => void;
}

const CONTEXT_COMPLETENESS_LABELS: Record<string, string> = {
  context: "Operator context",
  guidance: "Staff guidance",
  source_rule: "Source rule",
  sourceRule: "Source rule",
  meaningful_metadata: "Meaningful metadata",
  meaningfulMetadata: "Meaningful metadata",
};

export function CrmTaskContextQualityCard({ contextCompleteness }: CrmTaskContextQualityCardProps) {
  const status = getCrmTaskContextCompletenessStatus(contextCompleteness);
  const message = getCrmTaskContextCompletenessMessage(contextCompleteness);

  if (!contextCompleteness || !status || !message) return null;

  return (
    <div className={cn(
      "mb-3 rounded-md border p-3",
      status === "ready"
        ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/10"
        : status === "partial"
          ? "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10"
          : "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10",
    )}>
      <p className={cn(
        "text-xs font-semibold uppercase tracking-[0.12em]",
        status === "ready"
          ? "text-[var(--color-success)]"
          : status === "partial"
            ? "text-[var(--color-warning)]"
            : "text-[var(--color-destructive)]",
      )}>
        Context quality
      </p>
      <p className="mt-1 text-sm text-[var(--color-foreground)]">{message}</p>
      {contextCompleteness.missing.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {contextCompleteness.missing.map((missing) => (
            <span
              key={missing}
              className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]"
            >
              Missing: {formatCrmTaskContextCompletenessLabel(missing)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function CrmTaskAssigneeRoutingCard({
  assigneeLabel,
  assigneeDraftId,
  assigneeOptions,
  isAssigneeDraftKnown,
  isClosed,
  isUpdatingAssignee,
  hasAssigneeDraftChanged,
  feedback,
  onAssigneeDraftChange,
  onSaveAssignee,
}: CrmTaskAssigneeRoutingCardProps) {
  return (
    <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Assignee routing
          </p>
          <p className="mt-1 text-sm text-[var(--color-foreground)]">
            {assigneeLabel ?? "Unassigned"}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            aria-label="Assign task detail assignee"
            value={assigneeDraftId}
            onChange={(event) => onAssigneeDraftChange(event.target.value)}
            disabled={isClosed || isUpdatingAssignee}
            className="min-h-[40px] rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="">Unassigned</option>
            {!isAssigneeDraftKnown && (
              <option value={assigneeDraftId}>{assigneeDraftId}</option>
            )}
            {assigneeOptions.map((member) => (
              <option key={member.id} value={member.id}>
                {member.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={onSaveAssignee}
            disabled={isClosed || !hasAssigneeDraftChanged || isUpdatingAssignee}
            className="inline-flex min-h-[40px] items-center justify-center rounded-md border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isUpdatingAssignee ? "Saving assignee..." : "Save assignee"}
          </button>
        </div>
      </div>
      {isClosed && (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Closed tasks keep their historical assignee for audit.
        </p>
      )}
      {feedback && (
        <p className="mt-2 text-xs text-[var(--color-success)]">{feedback}</p>
      )}
    </div>
  );
}

export function getCrmTaskContextCompletenessStatus(
  completeness: CrmTaskContextCompleteness | null | undefined,
): CrmTaskContextCompletenessStatus | null {
  if (!completeness) return null;
  if (!completeness.isUsable) return "missing";
  return completeness.missing.length > 0 ? "partial" : "ready";
}

export function getCrmTaskContextCompletenessMessage(
  completeness: CrmTaskContextCompleteness | null | undefined,
): string | null {
  if (!completeness) return null;
  if (!completeness.isUsable) {
    return "This task is missing usable operator context. Review the source event or repair the imported task before relying on it for staff work.";
  }
  if (completeness.missing.length > 0) {
    return "This task has enough context to work, but some review signals are still missing.";
  }
  return "This task has operator context, guidance, source evidence, and meaningful metadata.";
}

export function formatCrmTaskContextCompletenessLabel(value: string): string {
  return CONTEXT_COMPLETENESS_LABELS[value] ?? formatCrmTaskDetailStateLabel(value) ?? value;
}

function formatCrmTaskDetailStateLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
